import React from "react";
import styled from "styled-components";
import { Link } from "gatsby";
import { Flex } from "reflexbox";
import { useStaticQuery, graphql } from "gatsby";
import useEffectOnce from "react-use/lib/useEffectOnce";

import * as colors from "../styles/colors";
import { AnnouncementBar } from "./AnnouncementBar";
import { useIsScrollTop } from "../hooks/useIsScrollTop";
import { HamburgerMenu, DesktopMenu } from "./Navigation";
import logo from "../images/logo.png";

const HeaderContainer = styled.header`
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  z-index: 3;
  overflow: hidden;
  background-color: rgba(0, 0, 0, 0);
  border-color: rgba(0, 0, 0, 0);
  transition: all 0.2s ease-in-out;

  &:not(.scroll-is-top) {
    backdrop-filter: blur(35px);
    background-color: rgba(0, 0, 0, 0.65);
    border-bottom: 2px solid rgba(255, 255, 255, 0.05);
  }

  // Since firefox doesn't support backdrop-filter
  @-moz-document url-prefix() {
    &:not(.scroll-is-top) {
      background-color: rgba(0, 0, 0, 0.95);
    }
  }
`;

export function Header({
  disableLogoLink = false,
  accentColor = colors.primaryGreen,
  accentGradient = colors.blueGradient,
  ctaBackgroundColor = colors.accent,
  ctaTextColor = colors.light
}) {
  const isScrollTop = useIsScrollTop();

  const {
    contentfulAnnouncementBar: announcementBar,
    shopifyProduct: productDetails,
    contentfulProductMetadata: productMetadata,
    contentfulNavigation: {
      menu: { links }
    },
    contentfulFooter: socialLinks
  } = useStaticQuery(
    graphql`
      query {
        contentfulAnnouncementBar(
          contentful_id: { eq: "2BYTDA47QG3cLLH8kCXeZV" }
        ) {
          show
          content {
            raw
          }
        }
        contentfulNavigation(
          contentful_id: { eq: "5EkY0XkADMmidQi6OQv3ly" }
        ) {
          title
          menu {
            links {
              title
              type
              path
            }
          }
        }
        contentfulProductMetadata(
          contentful_id: { eq: "3j9eOFwnPI4jJ0YPwK7ISB" }
        ) {
          name
          cta
          shopifyId
          path
        }
        shopifyProduct(
          shopifyId: { eq: "gid://shopify/Product/6540456296499" }
        ) {
          priceRangeV2 {
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          variants {
            shopifyId
            sku
            availableForSale
          }
          handle
          shopifyId
        }

        contentfulFooter(
          contentful_id: { eq: "3P2uuP4NMkK0934iuTeF4S" }
        ) {
          socialMenu {
            title
            links {
              id
              title
              layout
              type
              path
              openInNewTab
            }
          }
        }
      }
    `
  );

  useEffectOnce(() => {
    const productPages = ["/", "/tech-specs", "/notion"];
    if (
      typeof window !== "undefined" &&
      window.gtag &&
      productPages.includes(window.location.pathname)
    ) {
      window.gtag("event", "view_item", {
        value: productDetails.priceRangeV2.maxVariantPrice.amount,
        items: [
          {
            id: productDetails.variants[0].sku
          }
        ]
      });
    }
  }, [productDetails]);

  const logoProps = {
    as: "img",
    alignSelf: "flex-start",
    py: "20px",
    width: ["140px", "160px", "200px"],
    alt: "Neurosity",
    src: logo
  };

  const showAnnouncementBar = !!announcementBar?.show;

  return (
    <>
      <HeaderContainer
        className={`${isScrollTop ? "scroll-is-top" : ""} ${
          showAnnouncementBar ? "announcement-bar" : ""
        }`}
      >
        <AnnouncementBar announcementBar={announcementBar} />
        <Flex
          minHeight="78px"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          padding={[
            "0px 60px 0px 20px",
            "0px 60px 0px 20px",
            "0px 40px 0px 30px"
          ]}
        >
          {disableLogoLink ? (
            <Flex {...logoProps} style={{ cursor: "pointer" }} />
          ) : (
            <Link to="/" style={{ display: "flex" }}>
              <Flex {...logoProps} />
            </Link>
          )}
          <Flex>
            <DesktopMenu
              accentColor={accentColor}
              accentGradient={accentGradient}
              ctaBackgroundColor={ctaBackgroundColor}
              ctaTextColor={ctaTextColor}
              links={links}
              cta={true}
              productMetadata={productMetadata}
            />
          </Flex>
        </Flex>
      </HeaderContainer>
      <HamburgerMenu
        socialLinks={socialLinks.socialMenu}
        links={links}
        productMetadata={productMetadata}
      />
    </>
  );
}
